import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayviseApiError } from "@dayvise/client-sdk";
import { formatBytes } from "@dayvise/react-ui";
import { useActiveWorkspaceId, useDayviseClient, useSession } from "./providers.tsx";

/** Image types a message may carry, mirroring the control plane's allowlist. */
export const DRAFT_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;

/** One attachment may not exceed this; the server enforces the same ceiling. */
export const DRAFT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const DRAFT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export const DRAFT_ATTACHMENT_MAX_COUNT = 8;

const TEXT_MIME_TYPES = new Set([
  "text/markdown",
  "text/plain",
  "text/csv",
  "application/json",
  "application/x-ndjson",
  "application/yaml",
  "application/x-yaml",
  "application/xml",
  "text/xml",
]);

const EXTENSION_MIME_TYPES: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  md: "text/markdown",
  markdown: "text/markdown",
  txt: "text/plain",
  csv: "text/csv",
  json: "application/json",
  yaml: "application/yaml",
  yml: "application/yaml",
  xml: "application/xml",
  ndjson: "application/x-ndjson",
  ts: "text/plain",
  tsx: "text/plain",
  js: "text/plain",
  jsx: "text/plain",
  py: "text/plain",
  rs: "text/plain",
  go: "text/plain",
  java: "text/plain",
  kt: "text/plain",
  sql: "text/plain",
  sh: "text/plain",
  toml: "text/plain",
  ini: "text/plain",
};

const UNSAFE_NAME = /[\\/:<>"|?*\u0000-\u001f\u007f]/g;

export type DraftAttachmentStatus = "uploading" | "ready" | "error";

export interface DraftAttachment {
  readonly draftId: string;
  readonly name: string;
  readonly path: string;
  readonly mimeType: string;
  readonly kind: "image" | "file";
  readonly size: number;
  readonly status: DraftAttachmentStatus;
  readonly error: string | null;
  readonly fileId: string | null;
  readonly version: number | null;
  readonly previewUrl: string | null;
  readonly canRetry: boolean;
}

export interface DraftAttachmentSelection {
  readonly fileId: string;
  readonly version: number;
  readonly name: string;
}

export function attachmentMimeType(file: { readonly name: string; readonly type: string }): string | null {
  const declared = file.type.split(";")[0]!.trim().toLowerCase();
  const extension = file.name.includes(".") ? (file.name.split(".").at(-1) ?? "").toLowerCase() : "";
  if (declared.length > 0) {
    if ((DRAFT_IMAGE_MIME_TYPES as readonly string[]).includes(declared)) return declared;
    if (TEXT_MIME_TYPES.has(declared)) return declared;
    return null;
  }
  return EXTENSION_MIME_TYPES[extension] ?? null;
}

/** Why the composer refuses to send yet, or null when the draft is ready. */
export function attachmentSendBlockedReason(input: { readonly pending: boolean | number; readonly failed: boolean | number; readonly count: number }): string | null {
  const failed = input.failed === true || (typeof input.failed === "number" && input.failed > 0);
  const pending = input.pending === true || (typeof input.pending === "number" && input.pending > 0);
  if (failed) return "Remove or retry the attachment that failed before sending.";
  if (pending) return "Wait for the attachment to finish uploading before sending.";
  if (input.count > DRAFT_ATTACHMENT_MAX_COUNT) return `Attach at most ${DRAFT_ATTACHMENT_MAX_COUNT} files per message.`;
  return null;
}

export function attachmentProblem(file: { readonly name: string; readonly size: number; readonly type: string }): string | null {
  if (file.size <= 0) return `${file.name} is empty; attach a file with content.`;
  if (attachmentMimeType(file)?.startsWith("image/") && file.size > DRAFT_IMAGE_MAX_BYTES) {
    return `${file.name} is ${formatBytes(file.size)}; an image for model input may be at most ${formatBytes(DRAFT_IMAGE_MAX_BYTES)}.`;
  }
  if (file.size > DRAFT_ATTACHMENT_MAX_BYTES) {
    return `${file.name} is ${formatBytes(file.size)}; a single attachment may be at most ${formatBytes(DRAFT_ATTACHMENT_MAX_BYTES)}.`;
  }
  if (attachmentMimeType(file) === null) return `${file.name} is neither a supported image nor a text file.`;
  return null;
}

/**
 * The API's machine-readable code, whether the failure arrived as a thrown
 * DayviseApiError or as the decoded error body of a wrapped response.
 */
function attachmentErrorCode(error: unknown): string | null {
  if (error instanceof DayviseApiError) return error.code;
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { readonly code: unknown }).code;
    if (typeof code === "string") return code;
  }
  return null;
}

function attachmentErrorMessage(error: unknown): string | null {
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { readonly message: unknown }).message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return null;
}

export function describeAttachmentError(error: unknown): string {
  const code = attachmentErrorCode(error);
  if (code === "image_too_large") return "The image is larger than this workspace accepts for input.";
  if (code === "unsupported_image_type") return "That image type is not supported for model input.";
  if (code === "quota_exceeded" || code === "storage_quota_exceeded") return "This workspace is out of storage; free space before attaching.";
  if (code === "entitlement_denied") return "This workspace's plan does not allow that attachment.";
  if (code === "aborted") return "The upload was cancelled.";
  return attachmentErrorMessage(error) ?? "The attachment could not be uploaded.";
}

export function describeAttachmentSelectionError(error: unknown): string | null {
  const code = attachmentErrorCode(error);
  if (code === "file_revision_stale") return "A selected file changed after it was chosen. Remove it and select the current revision.";
  if (code === "file_revision_unavailable") return "A selected file was deleted. Remove it and choose another file.";
  if (code === "file_not_found") return "A selected file is no longer readable here. Remove it and choose another file.";
  if (code === "file_selection_conflict") return "Two selected files share one path. Keep a single revision per path.";
  if (code === "image_too_large" || code === "unsupported_image_type") return "A selected image is no longer accepted for model input. Remove it and choose another.";
  return null;
}

/** Keep every refusal the user has not dismissed, without duplicating a notice. */
function appendRejected(previous: readonly string[], added: readonly string[]): readonly string[] {
  const missing = added.filter((problem) => !previous.includes(problem));
  return missing.length === 0 ? previous : [...previous, ...missing];
}

interface DraftState {
  readonly identity: string;
  readonly entries: readonly DraftAttachment[];
  readonly rejected: readonly string[];
}

/**
 * Attachments for one unsent message.
 *
 * Everything here is conversation-scoped: a draft only exists for the
 * conversation and branch it was created in, so switching either one discards the
 * pending selection instead of carrying bytes into an unrelated run. Uploaded
 * files are retained workspace files; removing a draft entry never deletes them.
 */
export function useDraftAttachments(options: { conversationId: string | null; branchId?: string | null; disabled?: boolean }) {
  const { conversationId, branchId = null, disabled = false } = options;
  const client = useDayviseClient();
  const workspaceId = useActiveWorkspaceId();
  const { session } = useSession();
  const identity = JSON.stringify([workspaceId, session.account?.userId ?? null, conversationId, branchId ?? null]);
  const current = useRef(identity);
  const controllers = useRef(new Map<string, AbortController>());
  const previews = useRef(new Map<string, string>());
  const sources = useRef(new Map<string, File>());
  const [state, setState] = useState<DraftState>({ identity, entries: [], rejected: [] });

  if (current.current !== identity) {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    for (const url of previews.current.values()) URL.revokeObjectURL(url);
    previews.current.clear();
    sources.current.clear();
    current.current = identity;
  }

  useEffect(() => () => {
    for (const controller of controllers.current.values()) controller.abort();
    for (const url of previews.current.values()) URL.revokeObjectURL(url);
    previews.current.clear();
    sources.current.clear();
  }, []);

  const visible = state.identity === identity ? state : { identity, entries: [], rejected: [] };

  const patch = useCallback((draftId: string, change: Partial<DraftAttachment>) => {
    setState((previous) => previous.identity === identity
      ? { ...previous, entries: previous.entries.map((entry) => (entry.draftId === draftId ? { ...entry, ...change } : entry)) }
      : previous);
  }, [identity]);

  const upload = useCallback(async (draftId: string, file: File) => {
    if (conversationId === null) return;
    const controller = new AbortController();
    controllers.current.set(draftId, controller);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const basename = file.name.replace(UNSAFE_NAME, "_").slice(-160) || "attachment";
      const path = `uploads/${conversationId}/${draftId}/${basename}`;
      const result = await client.files.upload({ path, bytes, contentType: attachmentMimeType(file) ?? "application/octet-stream", conversationId }, { signal: controller.signal });
      if (current.current !== identity || controller.signal.aborted) return;
      patch(draftId, { status: "ready", error: null, fileId: result.file.fileId, version: result.file.version, size: result.file.size });
    } catch (error) {
      if (current.current !== identity || controller.signal.aborted) return;
      patch(draftId, { status: "error", error: describeAttachmentError(error), canRetry: sources.current.has(draftId) });
    } finally {
      if (controllers.current.get(draftId) === controller) controllers.current.delete(draftId);
    }
  }, [client, conversationId, identity, patch]);

  const addFiles = useCallback((files: readonly File[]) => {
    if (conversationId === null || disabled) return;
    const room = DRAFT_ATTACHMENT_MAX_COUNT - visible.entries.length;
    const rejected: string[] = [];
    const accepted: { draftId: string; file: File }[] = [];
    for (const file of files) {
      const problem = attachmentProblem(file);
      if (problem !== null) { rejected.push(problem); continue; }
      if (accepted.length >= room) { rejected.push(`${file.name} was not added: at most ${DRAFT_ATTACHMENT_MAX_COUNT} attachments can ride with one message.`); continue; }
      accepted.push({ draftId: crypto.randomUUID(), file });
    }
    if (accepted.length === 0) {
      if (rejected.length > 0) setState((previous) => previous.identity === identity ? { ...previous, rejected: appendRejected(previous.rejected, rejected) } : previous);
      return;
    }
    setState((previous) => previous.identity === identity ? {
      ...previous,
      rejected: appendRejected(previous.rejected, rejected),
      entries: [...previous.entries, ...accepted.map(({ draftId, file }) => {
        sources.current.set(draftId, file);
        const mimeType = attachmentMimeType(file)!;
        const previewUrl = mimeType.startsWith("image/") ? URL.createObjectURL(file) : null;
        if (previewUrl !== null) previews.current.set(draftId, previewUrl);
        return {
          draftId, name: file.name, path: file.name, mimeType, kind: mimeType.startsWith("image/") ? "image" as const : "file" as const,
          size: file.size, status: "uploading" as const, error: null, fileId: null, version: null, previewUrl, canRetry: false,
        };
      })],
    } : previous);
    for (const { draftId, file } of accepted) void upload(draftId, file);
  }, [conversationId, disabled, identity, upload, visible.entries.length]);

  const remove = useCallback((draftId: string) => {
    const controller = controllers.current.get(draftId);
    controller?.abort();
    controllers.current.delete(draftId);
    const preview = previews.current.get(draftId);
    if (preview !== undefined) { URL.revokeObjectURL(preview); previews.current.delete(draftId); }
    sources.current.delete(draftId);
    setState((previous) => previous.identity === identity
      ? { ...previous, entries: previous.entries.filter((entry) => entry.draftId !== draftId) }
      : previous);
  }, [identity]);

  const retry = useCallback((draftId: string) => {
    const file = sources.current.get(draftId);
    if (file === undefined || disabled || conversationId === null) return;
    patch(draftId, { status: "uploading", error: null, canRetry: false });
    void upload(draftId, file);
  }, [conversationId, disabled, patch, upload]);

  const clear = useCallback(() => {
    for (const controller of controllers.current.values()) controller.abort();
    controllers.current.clear();
    for (const url of previews.current.values()) URL.revokeObjectURL(url);
    previews.current.clear();
    sources.current.clear();
    setState((previous) => previous.identity === identity ? { identity, entries: [], rejected: [] } : previous);
  }, [identity]);

  const dismissRejected = useCallback(() => {
    setState((previous) => previous.identity === identity ? { ...previous, rejected: [] } : previous);
  }, [identity]);

  const ready = useMemo<readonly DraftAttachmentSelection[]>(() => visible.entries
    .filter((entry) => entry.status === "ready" && entry.fileId !== null && entry.version !== null)
    .map((entry) => ({ fileId: entry.fileId!, version: entry.version!, name: entry.name })), [visible.entries]);
  const pending = visible.entries.some((entry) => entry.status === "uploading");
  const failed = visible.entries.filter((entry) => entry.status === "error").length;

  return {
    attachments: visible.entries,
    rejected: visible.rejected,
    ready,
    pending,
    failed,
    hasAttachments: visible.entries.length > 0,
    addFiles,
    remove,
    retry,
    clear,
    dismissRejected,
  };
}
